import { camelCase } from 'lodash';

type Primitive = bigint | boolean | number | null | string | symbol | undefined;
type AnyFunction = (...args: never[]) => unknown;
type NonTransformable = AnyFunction | Date | RegExp;

type CamelToSnakeCase<S extends string> = S extends `${infer Head}${infer Tail}`
  ? Tail extends Uncapitalize<Tail>
    ? `${Lowercase<Head>}${CamelToSnakeCase<Tail>}`
    : `${Lowercase<Head>}_${CamelToSnakeCase<Tail>}`
  : S;

type SnakeToCamelCase<S extends string> =
  S extends `${infer Head}_${infer Tail}`
    ? `${Head}${Capitalize<SnakeToCamelCase<Tail>>}`
    : S;

export type SnakeCasedPropertiesDeep<T> = T extends Primitive | NonTransformable
  ? T
  : T extends readonly (infer U)[]
  ? SnakeCasedPropertiesDeep<U>[]
  : T extends object
  ? {
      [K in keyof T as K extends string
        ? CamelToSnakeCase<K>
        : K]: SnakeCasedPropertiesDeep<T[K]>;
    }
  : T;

export type CamelCasedPropertiesDeep<T> = T extends Primitive | NonTransformable
  ? T
  : T extends readonly (infer U)[]
  ? CamelCasedPropertiesDeep<U>[]
  : T extends object
  ? {
      [K in keyof T as K extends string
        ? SnakeToCamelCase<K>
        : K]: CamelCasedPropertiesDeep<T[K]>;
    }
  : T;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const prototype = Object.getPrototypeOf(value);

  return prototype === Object.prototype || prototype === null;
}

function convertKeysDeep(
  value: unknown,
  transformKey: (key: string) => string,
): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => convertKeysDeep(item, transformKey));
  }

  if (!isPlainObject(value)) {
    return value;
  }

  return Object.fromEntries(
    Object.entries(value).map(([key, nestedValue]) => [
      transformKey(key),
      convertKeysDeep(nestedValue, transformKey),
    ]),
  );
}

export function camelCaseKeysDeep<T>(value: SnakeCasedPropertiesDeep<T>): T;
export function camelCaseKeysDeep<T>(value: T): CamelCasedPropertiesDeep<T>;
export function camelCaseKeysDeep(value: unknown): unknown {
  return convertKeysDeep(value, camelCase);
}
